import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Switch,
	Table,
	TableBody,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import SubjectIcon from "@material-ui/icons/Subject";
import DestinationContentDialog from "./DestinationContentDialog";
import {
	BookingContentName,
	BookingContentWrap,
	BookingFilterButton,
	MyBookingWrapper,
} from "../MyBooking/styled/DesStyled";
import { StyledSortableItemDivWrap } from "../AdminPaymentChannelNumber/styled/PaymentNumber";
import { CommonTableHeadCell } from "../../controls/components/commonTable/CommonTable";
import {
	LocationTableBodyAdminContentTd,
	LocationTableBodyAdminContentTr,
} from "../LocationManage/styled/LocationStyled";
import {
	ensureDestinationFromWorldLibrary,
	deleteDestination,
	getTopDestinationConfig,
	reorderDestinations,
	toggleDestinationActive,
	updateDestinationCoordinates,
	updateDestinationMeta,
	uploadDestinationImage,
	EnsureFromWorldLibraryPayload,
} from "../../../services/TopDestinationService";
import useAlertDialog from "../../hooks/useAlertDialog";
import { AlertType } from "../../hooks/common/useAttachAlertDialog";
import {
	DestinationSuggestionPopper,
	mergeInputPropsNoBrowserAutofill,
} from "../../components/WorldLibraryLocationPicker";
import { type WorldCityOption } from "../../../utils/searchWorldLibraryCities";
import { searchGeonamePlacesForAdmin } from "../../../services/geonamePlaceSearchService";
import PermissionGate from "../../controls/auth/PermissionGate";

function ImageDropZone({ imageUrl, onImageUploaded }: { imageUrl: string; onImageUploaded: (url: string) => void }) {
	const [uploading, setUploading] = useState(false);
	const fileRef = useRef<HTMLInputElement>(null);

	const handleFile = useCallback(
		async (file: File) => {
			if (!file.type.startsWith("image/")) return;
			setUploading(true);
			try {
				const url = await uploadDestinationImage(file);
				if (url) onImageUploaded(url);
			} finally {
				setUploading(false);
			}
		},
		[onImageUploaded]
	);

	return (
		<div
			onClick={() => fileRef.current?.click()}
			style={{
				border: "2px dashed #d1d5db",
				borderRadius: "8px",
				padding: "8px",
				cursor: "pointer",
				minHeight: "80px",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}>
			<input
				ref={fileRef}
				type="file"
				accept="image/*"
				style={{ display: "none" }}
				onChange={(e) => {
					const file = e.target.files?.[0];
					if (file) handleFile(file);
					e.target.value = "";
				}}
			/>
			{uploading ? (
				<CircularProgress size={24} />
			) : imageUrl ? (
				<img
					src={imageUrl}
					alt=""
					style={{ maxWidth: "120px", maxHeight: "70px", borderRadius: 6 }}
				/>
			) : (
				<CloudUploadIcon style={{ fontSize: 28, color: "#9ca3af" }} />
			)}
		</div>
	);
}

function SortableDestinationTableRow({
	val,
	stt,
	onImage,
	onToggle,
	onEditCoords,
	onEditContent,
	onHomeChips,
	onTopBlock,
	onDelete,
}: {
	val: {
		id: string;
		name?: string;
		city?: string;
		country?: string;
		lat?: number;
		long?: number;
		image?: string;
		active?: boolean;
		showInHomeChips?: boolean;
		showInTopBlock?: boolean;
	};
	stt: number;
	onImage: (id: string, url: string) => void;
	onToggle: (row: { id: string; active?: boolean }, next: boolean) => void;
	onEditCoords: () => void;
	onEditContent: () => void;
	onHomeChips: (id: string, next: boolean) => void;
	onTopBlock: (id: string, next: boolean) => void;
	onDelete: (id: string) => void;
}) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: val.id });
	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.65 : 1,
	};
	return (
		<LocationTableBodyAdminContentTr
			ref={setNodeRef}
			style={style}
			{...attributes}>
			<LocationTableBodyAdminContentTd
				align="center"
				style={{ width: 48 }}>
				<IconButton
					size="small"
					aria-label="Kéo để sắp thứ tự"
					style={{ cursor: "grab" }}
					{...listeners}>
					<DragIndicatorIcon fontSize="small" />
				</IconButton>
			</LocationTableBodyAdminContentTd>
			<LocationTableBodyAdminContentTd align="center">{stt}</LocationTableBodyAdminContentTd>
			<LocationTableBodyAdminContentTd align="center">
				<ImageDropZone
					imageUrl={val.image || ""}
					onImageUploaded={(url) => onImage(val.id, url)}
				/>
			</LocationTableBodyAdminContentTd>
			<LocationTableBodyAdminContentTd align="center">
				<div style={{ fontWeight: 600 }}>{val.id}</div>
				<div>{val.city || val.name}</div>
			</LocationTableBodyAdminContentTd>
			<LocationTableBodyAdminContentTd align="center">{val.country}</LocationTableBodyAdminContentTd>
			<LocationTableBodyAdminContentTd align="center">
				{val.lat} / {val.long}
			</LocationTableBodyAdminContentTd>
			<LocationTableBodyAdminContentTd align="center">
				<PermissionGate
					permission="documents.manage"
					fallback={
						<Switch
							checked={val.showInHomeChips !== false}
							color="primary"
							size="small"
							disabled
						/>
					}>
					<Switch
						checked={val.showInHomeChips !== false}
						onChange={(_e, checked) => onHomeChips(val.id, checked)}
						color="primary"
						size="small"
					/>
				</PermissionGate>
			</LocationTableBodyAdminContentTd>
			<LocationTableBodyAdminContentTd align="center">
				<PermissionGate
					permission="documents.manage"
					fallback={
						<Switch
							checked={val.showInTopBlock !== false}
							color="primary"
							size="small"
							disabled
						/>
					}>
					<Switch
						checked={val.showInTopBlock !== false}
						onChange={(_e, checked) => onTopBlock(val.id, checked)}
						color="primary"
						size="small"
					/>
				</PermissionGate>
			</LocationTableBodyAdminContentTd>
			<LocationTableBodyAdminContentTd align="center">
				<PermissionGate
					permission="documents.manage"
					fallback={
						<Switch
							checked={val.active !== false}
							color="primary"
							disabled
						/>
					}>
					<Switch
						checked={val.active !== false}
						onChange={(_e, checked) => onToggle(val, checked)}
						color="primary"
					/>
				</PermissionGate>
			</LocationTableBodyAdminContentTd>
			<LocationTableBodyAdminContentTd align="center">
				<PermissionGate permission="documents.manage">
					<IconButton
						onClick={onEditCoords}
						aria-label="edit coords"
						size="small">
						<EditOutlinedIcon fontSize="small" />
					</IconButton>
					<IconButton
						onClick={onEditContent}
						aria-label="edit content"
						size="small">
						<SubjectIcon fontSize="small" />
					</IconButton>
					<IconButton
						onClick={() => onDelete(val.id)}
						aria-label="delete destination"
						size="small">
						<DeleteOutlineIcon fontSize="small" />
					</IconButton>
				</PermissionGate>
			</LocationTableBodyAdminContentTd>
		</LocationTableBodyAdminContentTr>
	);
}

export default function AdminTopDestinationManage() {
	const [listLocation, setListLocation] = useState<any[]>([]);
	const [tableFilter, setTableFilter] = useState("");
	const [worldInput, setWorldInput] = useState("");
	const [worldDebounced, setWorldDebounced] = useState("");
	const [worldOptions, setWorldOptions] = useState<WorldCityOption[]>([]);
	const [worldSearchLoading, setWorldSearchLoading] = useState(false);
	const [selectedWorld, setSelectedWorld] = useState<WorldCityOption | null>(null);
	const [coordEdit, setCoordEdit] = useState<{ id: string; lat: string; long: string } | null>(null);
	const [contentDialogDest, setContentDialogDest] = useState<any>(null);
	const { openAlertDialog } = useAlertDialog();

	const fetchLocations = async () => {
		try {
			const data = await getTopDestinationConfig();
			setListLocation(Array.isArray(data) ? data : []);
		} catch {
			setListLocation([]);
		}
	};

	useEffect(() => {
		void fetchLocations();
	}, []);

	const dndSensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
		useSensor(KeyboardSensor)
	);

	useEffect(() => {
		const t = window.setTimeout(() => setWorldDebounced(worldInput), 350);
		return () => window.clearTimeout(t);
	}, [worldInput]);

	useEffect(() => {
		const q = worldDebounced.trim();
		if (q.length < 2) {
			setWorldOptions([]);
			return;
		}
		let cancel = false;
		setWorldSearchLoading(true);
		void (async () => {
			try {
				const rows = await searchGeonamePlacesForAdmin(q, { limit: 100 });
				if (!cancel) {
					setWorldOptions(rows);
				}
			} catch {
				if (!cancel) {
					setWorldOptions([]);
				}
			} finally {
				if (!cancel) {
					setWorldSearchLoading(false);
				}
			}
		})();
		return () => {
			cancel = true;
		};
	}, [worldDebounced]);

	const filteredList = useMemo(() => {
		const q = tableFilter.trim().toLowerCase();
		if (!q) return listLocation;
		return listLocation.filter((row) => {
			const hay = `${row.id} ${row.name} ${row.country} ${row.city ?? ""}`.toLowerCase();
			return hay.includes(q);
		});
	}, [listLocation, tableFilter]);

	const handleAddFromWorldLibrary = async () => {
		if (!selectedWorld) {
			openAlertDialog?.(AlertType.Fail, "Chọn một dòng từ gợi ý (thành phố, quốc gia) rồi bấm thêm.", () => {});
			return;
		}
		const payload: EnsureFromWorldLibraryPayload = {
			countryCode: selectedWorld.countryCode,
			cityName: selectedWorld.cityName,
			lat: selectedWorld.lat,
			long: selectedWorld.long,
			timezone: selectedWorld.timezone,
			geonameId: selectedWorld.geonameId,
		};
		try {
			await ensureDestinationFromWorldLibrary(payload);
			openAlertDialog?.(AlertType.Success, "Đã thêm destination.", () => {});
			setSelectedWorld(null);
			setWorldInput("");
			setWorldDebounced("");
			await fetchLocations();
		} catch {
			openAlertDialog?.(AlertType.Fail, "Không thêm được destination.", () => {});
		}
	};

	const persistToggle = async (row: any, next: boolean) => {
		try {
			await toggleDestinationActive(row.id, next);
			await fetchLocations();
		} catch {
			openAlertDialog?.(AlertType.Fail, "Không cập nhật được trạng thái.", () => {});
		}
	};

	const persistCoordinates = async () => {
		if (!coordEdit) return;
		const lat = parseFloat(coordEdit.lat);
		const lng = parseFloat(coordEdit.long);
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
			openAlertDialog?.(AlertType.Fail, "Lat/Long không hợp lệ.", () => {});
			return;
		}
		try {
			await updateDestinationCoordinates(coordEdit.id, lat, lng);
			setCoordEdit(null);
			await fetchLocations();
			openAlertDialog?.(AlertType.Success, "Đã cập nhật tọa độ.", () => {});
		} catch {
			openAlertDialog?.(AlertType.Fail, "Lỗi khi lưu tọa độ.", () => {});
		}
	};

	const handleDestinationDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const str = (v: string | number) => String(v);
		const oldIndex = listLocation.findIndex((r) => str(r.id) === str(active.id));
		const newIndex = listLocation.findIndex((r) => str(r.id) === str(over.id));
		if (oldIndex < 0 || newIndex < 0) return;
		const reordered = arrayMove(listLocation, oldIndex, newIndex);
		const nextIds = reordered.map((r) => r.id);
		try {
			await reorderDestinations(nextIds);
			await fetchLocations();
		} catch (err: unknown) {
			const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
			const msg =
				data?.message && typeof data.message === "string" ? data.message : "Không sắp xếp được — thử lại.";
			openAlertDialog?.(AlertType.Fail, msg, () => {});
		}
	};

	const handleImageRow = async (id: string, url: string) => {
		try {
			await updateDestinationMeta(id, { image: url });
			await fetchLocations();
		} catch {
			openAlertDialog?.(AlertType.Fail, "Không cập nhật ảnh.", () => {});
		}
	};

	const persistHomeChips = async (id: string, next: boolean) => {
		try {
			await updateDestinationMeta(id, { showInHomeChips: next });
			await fetchLocations();
		} catch {
			openAlertDialog?.(AlertType.Fail, "Không cập nhật gợi ý dưới thanh tìm.", () => {});
		}
	};

	const persistTopBlock = async (id: string, next: boolean) => {
		try {
			await updateDestinationMeta(id, { showInTopBlock: next });
			await fetchLocations();
		} catch {
			openAlertDialog?.(AlertType.Fail, "Không cập nhật khối Top Destinations trên trang chủ.", () => {});
		}
	};

	const handleDeleteDestination = (id: string) => {
		openAlertDialog?.(
			AlertType.Confirm,
			"Xóa destination này? Hành động không hoàn tác.",
			() => {},
			() => {
				void (async () => {
					try {
						await deleteDestination(id);
						openAlertDialog?.(AlertType.Success, "Đã xóa destination.", () => {});
						await fetchLocations();
					} catch {
						openAlertDialog?.(AlertType.Fail, "Không xóa được destination.", () => {});
					}
				})();
			},
			undefined,
			{ declineText: "Hủy", acceptText: "Xóa" }
		);
	};

	return (
		<MyBookingWrapper>
			<BookingContentWrap>
				<StyledSortableItemDivWrap>
					<BookingContentName> Quản lí Top Destinations / Cities</BookingContentName>
					<div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-start" }}>
						<Autocomplete<WorldCityOption, false, false, false>
							value={selectedWorld}
							onChange={(_e, v) => {
								setSelectedWorld(v);
								if (v) setWorldInput(v.label);
							}}
							inputValue={worldInput}
							onInputChange={(_e, v, reason) => {
								setWorldInput(v);
								if (reason === "input" && selectedWorld && v !== selectedWorld.label) {
									setSelectedWorld(null);
								}
								if (reason === "clear") {
									setSelectedWorld(null);
								}
							}}
							options={worldOptions}
							getOptionLabel={(o) => o.label}
							getOptionSelected={(a, b) => Boolean(a && b && a.id === b.id)}
							filterOptions={(opts) => opts}
							PopperComponent={DestinationSuggestionPopper}
							noOptionsText={
								worldDebounced.trim().length < 2
									? "Nhập ít nhất 2 ký tự (dữ liệu GeoNames nằm ở bảng GeoNamePlaces trên server)"
									: worldSearchLoading
									? "Đang tìm…"
									: "Chưa có bản ghi. Nếu bạn mới import theo mã nước VN, các đô thị nước ngoài (ví dụ Paris) sẽ không tồn tại — cần import toàn cầu. Xem dòng hướng dẫn bên dưới."
							}
							style={{ minWidth: 320, maxWidth: 480, flex: "1 1 280px" }}
							renderInput={(params) => (
								<TextField
									{...params}
									size="small"
									label="Tìm thành phố (GeoNames)"
									placeholder="Gõ tên thành phố / quốc gia (dữ liệu DB)…"
									variant="outlined"
									inputProps={mergeInputPropsNoBrowserAutofill(
										params.inputProps as React.InputHTMLAttributes<HTMLInputElement>,
										"whalelo_top_dest_world_lib_search"
									)}
								/>
							)}
						/>
						<PermissionGate permission="documents.manage">
							<BookingFilterButton
								onClick={() => void handleAddFromWorldLibrary()}
								style={{ marginTop: 6 }}
								disabled={!selectedWorld}>
								Add
							</BookingFilterButton>
						</PermissionGate>
					</div>

					<div
						style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
						<TextField
							size="small"
							variant="outlined"
							label="Lọc bảng"
							placeholder="id, tên, quốc gia, thành phố…"
							value={tableFilter}
							onChange={(e) => setTableFilter(e.target.value)}
							style={{ minWidth: 260, maxWidth: 400 }}
						/>
					</div>
				</StyledSortableItemDivWrap>
				{tableFilter.trim() ? (
					<Typography
						variant="body2"
						color="textSecondary"
						style={{ marginTop: 4 }}>
						Đang lọc bảng. Xóa ô lọc để kéo thả sắp thứ tự hiển thị toàn bộ.
					</Typography>
				) : null}

				<Dialog
					open={Boolean(coordEdit)}
					onClose={() => setCoordEdit(null)}>
					<DialogTitle>Sửa Lat / Long</DialogTitle>
					<DialogContent>
						<div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 280 }}>
							<TextField
								label="Latitude"
								value={coordEdit?.lat ?? ""}
								onChange={(e) => coordEdit && setCoordEdit({ ...coordEdit, lat: e.target.value })}
							/>
							<TextField
								label="Longitude"
								value={coordEdit?.long ?? ""}
								onChange={(e) => coordEdit && setCoordEdit({ ...coordEdit, long: e.target.value })}
							/>
						</div>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setCoordEdit(null)}>Hủy</Button>
						<PermissionGate permission="documents.manage">
							<Button
								color="primary"
								variant="contained"
								onClick={() => void persistCoordinates()}>
								Lưu
							</Button>
						</PermissionGate>
					</DialogActions>
				</Dialog>

				{tableFilter.trim() ? (
					<div style={{ overflowX: "auto", marginTop: 16 }}>
						<Table aria-label="destinations">
							<TableHead>
								<TableRow>
									<CommonTableHeadCell
										align="center"
										style={{ width: 48 }}
									/>
									<CommonTableHeadCell align="center">STT</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Ảnh</CommonTableHeadCell>
									<CommonTableHeadCell align="center">ID &amp; City</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Country</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Lat/Long</CommonTableHeadCell>
									<CommonTableHeadCell
										align="center"
										style={{ minWidth: 110 }}>
										Gợi ý dưới thanh tìm
									</CommonTableHeadCell>
									<CommonTableHeadCell
										align="center"
										style={{ minWidth: 120 }}>
										Khối Top Destinations (trang chủ)
									</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Active</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Thao tác</CommonTableHeadCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredList.map((val, index) => (
									<LocationTableBodyAdminContentTr key={val.id}>
										<LocationTableBodyAdminContentTd align="center">
											—
										</LocationTableBodyAdminContentTd>
										<LocationTableBodyAdminContentTd align="center">
											{index + 1}
										</LocationTableBodyAdminContentTd>
										<LocationTableBodyAdminContentTd align="center">
											<ImageDropZone
												imageUrl={val.image || ""}
												onImageUploaded={(url) => void handleImageRow(val.id, url)}
											/>
										</LocationTableBodyAdminContentTd>
										<LocationTableBodyAdminContentTd align="center">
											<div style={{ fontWeight: 600 }}>{val.id}</div>
											<div>{val.city || val.name}</div>
										</LocationTableBodyAdminContentTd>
										<LocationTableBodyAdminContentTd align="center">
											{val.country}
										</LocationTableBodyAdminContentTd>
										<LocationTableBodyAdminContentTd align="center">
											{val.lat} / {val.long}
										</LocationTableBodyAdminContentTd>
										<LocationTableBodyAdminContentTd align="center">
											<PermissionGate
												permission="documents.manage"
												fallback={
													<Switch
														checked={val.showInHomeChips !== false}
														color="primary"
														size="small"
														disabled
													/>
												}>
												<Switch
													checked={val.showInHomeChips !== false}
													onChange={(_e, checked) => void persistHomeChips(val.id, checked)}
													color="primary"
													size="small"
												/>
											</PermissionGate>
										</LocationTableBodyAdminContentTd>
										<LocationTableBodyAdminContentTd align="center">
											<PermissionGate
												permission="documents.manage"
												fallback={
													<Switch
														checked={val.showInTopBlock !== false}
														color="primary"
														size="small"
														disabled
													/>
												}>
												<Switch
													checked={val.showInTopBlock !== false}
													onChange={(_e, checked) => void persistTopBlock(val.id, checked)}
													color="primary"
													size="small"
												/>
											</PermissionGate>
										</LocationTableBodyAdminContentTd>
										<LocationTableBodyAdminContentTd align="center">
											<PermissionGate
												permission="documents.manage"
												fallback={
													<Switch
														checked={val.active !== false}
														color="primary"
														disabled
													/>
												}>
												<Switch
													checked={val.active !== false}
													onChange={(_e, checked) => void persistToggle(val, checked)}
													color="primary"
												/>
											</PermissionGate>
										</LocationTableBodyAdminContentTd>
										<LocationTableBodyAdminContentTd align="center">
											<PermissionGate permission="documents.manage">
												<IconButton
													onClick={() =>
														setCoordEdit({
															id: val.id,
															lat: String(val.lat ?? ""),
															long: String(val.long ?? ""),
														})
													}
													aria-label="edit coords"
													size="small">
													<EditOutlinedIcon fontSize="small" />
												</IconButton>
												<IconButton
													onClick={() => setContentDialogDest(val)}
													aria-label="edit content"
													size="small">
													<SubjectIcon fontSize="small" />
												</IconButton>
												<IconButton
													onClick={() => handleDeleteDestination(val.id)}
													aria-label="delete destination"
													size="small">
													<DeleteOutlineIcon fontSize="small" />
												</IconButton>
											</PermissionGate>
										</LocationTableBodyAdminContentTd>
									</LocationTableBodyAdminContentTr>
								))}
							</TableBody>
						</Table>
					</div>
				) : (
					<DndContext
						sensors={dndSensors}
						collisionDetection={closestCenter}
						onDragEnd={(e) => void handleDestinationDragEnd(e)}>
						<div style={{ overflowX: "auto", marginTop: 16 }}>
							<Table aria-label="destinations">
								<TableHead>
									<TableRow>
										<CommonTableHeadCell
											align="center"
											style={{ width: 48 }}
										/>
										<CommonTableHeadCell align="center">STT</CommonTableHeadCell>
										<CommonTableHeadCell align="center">Ảnh</CommonTableHeadCell>
										<CommonTableHeadCell align="center">ID &amp; City</CommonTableHeadCell>
										<CommonTableHeadCell align="center">Country</CommonTableHeadCell>
										<CommonTableHeadCell align="center">Lat/Long</CommonTableHeadCell>
										<CommonTableHeadCell
											align="center"
											style={{ minWidth: 110 }}>
											Gợi ý dưới thanh tìm
										</CommonTableHeadCell>
										<CommonTableHeadCell
											align="center"
											style={{ minWidth: 120 }}>
											Khối Top Destinations (trang chủ)
										</CommonTableHeadCell>
										<CommonTableHeadCell align="center">Active</CommonTableHeadCell>
										<CommonTableHeadCell align="center">Thao tác</CommonTableHeadCell>
									</TableRow>
								</TableHead>
								<TableBody>
									<SortableContext
										items={listLocation.map((r) => r.id)}
										strategy={verticalListSortingStrategy}>
										{listLocation.map((val, index) => (
											<SortableDestinationTableRow
												key={val.id}
												val={val}
												stt={index + 1}
												onImage={handleImageRow}
												onToggle={persistToggle}
												onEditCoords={() =>
													setCoordEdit({
														id: val.id,
														lat: String(val.lat ?? ""),
														long: String(val.long ?? ""),
													})
												}
												onEditContent={() => setContentDialogDest(val)}
												onHomeChips={persistHomeChips}
												onTopBlock={persistTopBlock}
												onDelete={handleDeleteDestination}
											/>
										))}
									</SortableContext>
								</TableBody>
							</Table>
						</div>
					</DndContext>
				)}
			</BookingContentWrap>

			<DestinationContentDialog
				open={Boolean(contentDialogDest)}
				onClose={() => setContentDialogDest(null)}
				destination={contentDialogDest}
				onSaved={() => void fetchLocations()}
			/>
		</MyBookingWrapper>
	);
}
